import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, BarChart3, Tag, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LeadSourcesManager = () => {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [editLabel, setEditLabel] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const token = localStorage.getItem('rooftrack_token');
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const [srcRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/lead-sources?include_inactive=${showInactive}`, { headers }),
        axios.get(`${API}/api/lead-sources/stats`, { headers }),
      ]);
      setSources(srcRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    try {
      await axios.post(`${API}/api/lead-sources`, { label: newLabel.trim() }, { headers });
      setNewLabel('');
      setShowAdd(false);
      toast.success('Lead source added');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add source');
    }
  };

  const handleUpdate = async (id) => {
    if (!editLabel.trim()) return;
    try {
      await axios.put(`${API}/api/lead-sources/${id}`, { label: editLabel.trim() }, { headers });
      setEditingId(null);
      toast.success('Lead source updated');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const handleToggle = async (src) => {
    try {
      await axios.put(`${API}/api/lead-sources/${src.id}`, { active: !src.active }, { headers });
      toast.success(src.active ? 'Source deactivated' : 'Source activated');
      loadData();
    } catch (err) {
      toast.error('Failed to toggle source');
    }
  };

  const handleDelete = async (src) => {
    if (!window.confirm(`Delete "${src.label}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/lead-sources/${src.id}`, { headers });
      toast.success('Source deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatsForKey = (key) => stats.find(s => s.key === key) || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="lead-sources-manager">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/dashboard" className="text-sm text-gray-500 hover:text-orange-600 flex items-center gap-1 mb-1">
            <ArrowLeft className="w-3 h-3" /> Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="w-6 h-6 text-orange-600" />
            Lead Sources
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage lead generation sources to track campaign performance and disposition.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded"
            />
            Show inactive
          </label>
          <Button
            onClick={() => { setShowAdd(true); setNewLabel(''); }}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="add-source-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Add Source
          </Button>
        </div>
      </div>

      {/* Add Form */}
      {showAdd && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 flex items-end gap-3" data-testid="add-source-form">
          <div className="flex-1">
            <Label className="text-sm font-medium">New Source Name</Label>
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. HomeAdvisor, Yelp Ads, Storm Chasing"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              autoFocus
              data-testid="new-source-input"
            />
          </div>
          <Button onClick={handleAdd} className="bg-green-600 hover:bg-green-700" data-testid="save-source-btn">Save</Button>
          <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
        </div>
      )}

      {/* Sources Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <table className="w-full text-sm" data-testid="lead-sources-table">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-5 py-3 font-semibold text-gray-700">Source</th>
              <th className="text-left px-5 py-3 font-semibold text-gray-700">Key</th>
              <th className="text-center px-3 py-3 font-semibold text-gray-700">Show in Pipeline</th>
              <th className="text-center px-3 py-3 font-semibold text-gray-700">Total Leads</th>
              <th className="text-center px-3 py-3 font-semibold text-gray-700">Won</th>
              <th className="text-center px-3 py-3 font-semibold text-gray-700">Lost</th>
              <th className="text-center px-3 py-3 font-semibold text-gray-700">Active</th>
              <th className="text-right px-5 py-3 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sources.map((src) => {
              const st = getStatsForKey(src.key);
              return (
                <tr key={src.id} className={`border-b hover:bg-gray-50 ${!src.active ? 'opacity-50' : ''}`} data-testid={`source-row-${src.key}`}>
                  <td className="px-5 py-3">
                    {editingId === src.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="h-8 text-sm"
                          onKeyDown={(e) => { if (e.key === 'Enter') handleUpdate(src.id); if (e.key === 'Escape') setEditingId(null); }}
                          autoFocus
                        />
                        <Button size="sm" onClick={() => handleUpdate(src.id)} className="h-8 bg-green-600 hover:bg-green-700 text-xs px-2">Save</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="h-8 text-xs px-2">Cancel</Button>
                      </div>
                    ) : (
                      <span className="font-medium text-gray-900">{src.label}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-gray-500 font-mono text-xs">{src.key}</td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => handleToggle(src)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-colors ${src.active ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                      data-testid={`toggle-source-${src.key}`}
                      title={src.active ? 'Click to hide from pipeline' : 'Click to show in pipeline'}
                    >
                      {src.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                      {src.active ? 'Show' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-3 py-3 text-center font-semibold">{st.total_leads || 0}</td>
                  <td className="px-3 py-3 text-center text-green-600 font-medium">{st.won || 0}</td>
                  <td className="px-3 py-3 text-center text-red-500 font-medium">{st.lost || 0}</td>
                  <td className="px-3 py-3 text-center text-blue-600 font-medium">{st.active || 0}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => { setEditingId(src.id); setEditLabel(src.label); }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50"
                        title="Edit name"
                        data-testid={`edit-source-${src.key}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {!src.is_default && (
                        <button
                          onClick={() => handleDelete(src)}
                          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                          title="Delete"
                          data-testid={`delete-source-${src.key}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {sources.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-gray-400">No lead sources found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeadSourcesManager;
